"use client";

import { useState } from "react";
import { extractNationalDigits } from "@/shared/lib/phone";

type Props = {
  id?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (nationalDigits: string) => void;
  onInput?: () => void;
  onBlur?: () => void;
  required?: boolean;
  placeholder?: string;
  error?: boolean;
  /** Classes for the outer flex container - typically border/bg/shadow/rounding. */
  className?: string;
  /** Classes for the inner <input> - typically padding and text size. */
  inputClassName?: string;
};

// Country code is locked to DK. Opening this up means swapping the static
// prefix for a dropdown and passing the country to `extractNationalDigits`.
export function PhoneInput(props: Props) {
  const {
    id,
    name,
    onChange,
    onInput,
    onBlur,
    required,
    // Placeholder must itself pass isValidDanishNational - Danish subscriber
    // numbers never start with 0 or 1.
    placeholder = "20 12 34 56",
    error,
    className = "",
    inputClassName = "",
  } = props;

  const isControlled = props.value !== undefined;
  const [internal, setInternal] = useState(props.defaultValue ?? "");
  const value = isControlled ? props.value : internal;

  return (
    <div
      className={`flex items-stretch overflow-hidden ${className} ${
        error ? "ring-2 ring-[#CC0000]" : ""
      }`.trim()}
    >
      <span className="flex shrink-0 select-none items-center gap-1.5  bg-black/3 px-3 text-sm font-medium text-ink-muted">
        <span role="img" aria-label="Danmark" className="text-base leading-none">
          🇩🇰
        </span>
        <span aria-hidden>+45</span>
      </span>
      <input
        id={id}
        name={name}
        type="tel"
        inputMode="numeric"
        required={required}
        autoComplete="tel-national"
        placeholder={placeholder}
        value={value}
        onInput={onInput}
        onBlur={onBlur}
        onChange={(e) => {
          const next = extractNationalDigits(e.target.value);
          if (!isControlled) setInternal(next);
          onChange?.(next);
        }}
        className={`block w-full bg-transparent focus:outline-none border-2 border-black/10 ${inputClassName}`.trim()}
      />
    </div>
  );
}
